import { useEffect, useState } from "react";

/**
 * Returns a Date pinned to "today" in Europe/Rome and automatically updates
 * itself when local midnight rolls over — so a page left open past midnight
 * (Piera's laptop overnight, iPad on the counter, etc.) starts pointing at
 * the new day WITHOUT requiring the user to hit F5.
 *
 * The `dayKey` value ("YYYY-MM-DD" in Europe/Rome) is what changes on the
 * rollover: consumers can depend on `dayKey` in `useMemo` deps to recompute
 * "is current period" flags in real time.
 *
 * Implementation notes:
 *   - Uses a single `setTimeout` scheduled for the next midnight (in ms) —
 *     no per-second polling and no cost while the tab is idle.
 *   - Also refreshes when the tab becomes visible again (`visibilitychange`),
 *     which covers the "phone was asleep for 8h" case where JS timers pause.
 *   - Europe/Rome is hard-coded because this is a single-tenant app for
 *     Piera; if we later go multi-tenant we'll read `user.timezone`.
 */
const TZ = "Europe/Rome";

function romeDayKey(d = new Date()) {
  // sv-SE locale reliably formats YYYY-MM-DD; fall back to ISO if the runtime
  // ever removes it.
  try {
    return d.toLocaleDateString("sv-SE", { timeZone: TZ });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

function msUntilNextRomeMidnight(now = new Date()) {
  // Snapshot the current time in Europe/Rome, add one day, zero the wall
  // clock — then diff back to UTC milliseconds. Robust across DST.
  const romeNowIso = now.toLocaleString("en-CA", {
    timeZone: TZ,
    hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  // "2026-09-04, 21:35:12"  → parse
  const [datePart, timePart] = romeNowIso.split(", ");
  const [y, m, d] = datePart.split("-").map((n) => parseInt(n, 10));
  const [hh, mm, ss] = timePart.split(":").map((n) => parseInt(n, 10));
  const msSinceRomeMidnight = ((hh * 60 + mm) * 60 + ss) * 1000;
  const msInDay = 24 * 60 * 60 * 1000;
  // Add a 500ms safety margin so the timer never fires *just* before midnight.
  return msInDay - msSinceRomeMidnight + 500;
}

export default function useToday() {
  const [today, setToday] = useState(() => new Date());
  const [dayKey, setDayKey] = useState(() => romeDayKey());

  useEffect(() => {
    let timer;
    const schedule = () => {
      const delay = msUntilNextRomeMidnight();
      timer = setTimeout(() => {
        const now = new Date();
        setToday(now);
        setDayKey(romeDayKey(now));
        schedule();
      }, delay);
    };
    schedule();

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // The tab was backgrounded → setTimeout may have been throttled or
        // paused. Recompute immediately and reschedule.
        const now = new Date();
        const key = romeDayKey(now);
        setDayKey((prev) => (prev !== key ? key : prev));
        setToday(now);
        if (timer) clearTimeout(timer);
        schedule();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return { today, dayKey };
}
